export const MEDIA_STATUS = Object.freeze({
  BLOCKED: 'beat-0.4-blocked',
  REPLACEMENT_REQUIRED: 'beat-0.4-replacement-required',
  // Owner confirmed V01 as approved homepage media on September 24, 2026.
  OWNER_APPROVED: 'owner-approved-2026-09-24',
});

export const BRAND_STATUS = Object.freeze({
  PROVISIONAL_PREVIEW_ONLY: 'beat-0.4-provisional-preview-only',
});

export const BRAND_ASSETS = Object.freeze({
  mx4Logo: Object.freeze({
    id: 'M05',
    src: '/images/mx4/mx4-logo.webp',
    alt: 'MX4 logo',
    status: BRAND_STATUS.PROVISIONAL_PREVIEW_ONLY,
  }),
});

const assets = Object.freeze({
  cardioFloor: Object.freeze({
    id: 'F01',
    src: '/images/facility/cardio-room.jpg',
    subject: 'cardio floor and treadmills',
  }),
  adultTrainingFloor: Object.freeze({
    id: 'F03',
    src: '/images/facility/dsc_0640.jpg',
    subject: 'three adults on a strength-training floor',
  }),
  pilatesStudioAdults: Object.freeze({
    id: 'F04',
    src: '/images/facility/dsc_0769.jpg',
    subject: 'three adults in a Pilates equipment studio',
  }),
  groupBarbellClass: Object.freeze({
    id: 'F06',
    src: '/images/facility/fit-bright.jpg',
    subject: 'an adult group barbell class',
  }),
  emptyStrengthFloor: Object.freeze({
    id: 'F07',
    src: '/images/facility/green-room-1.jpg',
    subject: 'an empty strength-training floor',
  }),
  dumbbells: Object.freeze({
    id: 'F05',
    src: '/images/facility/dumbells.jpg',
    subject: 'dumbbells on a training floor',
  }),
  reformerStudio: Object.freeze({
    id: 'F08',
    src: '/images/facility/pilates-horz.jpg',
    subject: 'an empty Reformer Pilates studio',
  }),
  aquaticsDetail: Object.freeze({
    id: 'S01',
    src: '/images/swim/delta-valley-swim-hero.jpg',
    subject: 'goggles, towel, and flotation equipment beside a pool',
  }),
  mx4Group: Object.freeze({
    id: 'M02',
    src: '/images/mx4/mx4-group.webp',
    subject: 'an MX4 group training session',
  }),
  mx4Training: Object.freeze({
    id: 'M04',
    src: '/images/mx4/mx4-training.webp',
    subject: 'an MX4 training session',
  }),
  homepagePoster: Object.freeze({
    id: 'F02',
    src: '/images/facility/deltavalley_04.jpg',
    subject: 'two adults training with battle ropes',
  }),
  homepageMotion: Object.freeze({
    id: 'V01',
    src: '/hero-video.mp4',
    subject: 'adults exercising in club interiors',
  }),
  homepageFilmStill: Object.freeze({
    id: 'V01-S45',
    src: '/images/hero/delta-valley-film-still.jpg',
    subject: 'the strength-training floor, a still frame from V01 at 45 seconds',
  }),
});

export const PROHIBITED_MEDIA_CONTEXTS = Object.freeze({
  F03: Object.freeze(['basketball', 'kids-family', 'pickleball', 'rentals']),
  F04: Object.freeze(['aquatics', 'kids-family', 'pool', 'swimming']),
  F06: Object.freeze(['aquatics', 'kids-family', 'massage', 'partnerships', 'pickleball', 'recovery', 'rockwall']),
  F07: Object.freeze(['group-fitness', 'kids-family', 'locker-rooms', 'massage', 'recovery', 'success']),
});

const defineSlot = ({ id, context, label, asset = null, approved = false }) => {
  if (asset && PROHIBITED_MEDIA_CONTEXTS[asset.id]?.includes(context)) {
    throw new Error(`Media truth violation: ${asset.id} cannot be assigned to ${context}.`);
  }

  return Object.freeze({
    id,
    context,
    label,
    assetId: asset?.id || null,
    src: asset?.src || null,
    subject: asset?.subject || null,
    status: asset ? (approved ? MEDIA_STATUS.OWNER_APPROVED : MEDIA_STATUS.REPLACEMENT_REQUIRED) : MEDIA_STATUS.BLOCKED,
  });
};

export const MEDIA_SLOTS = Object.freeze({
  homeHeroMotion: defineSlot({ id: 'H01', context: 'homepage-hero', label: 'Homepage hero film', asset: assets.homepageMotion, approved: true }),
  homeHeroPoster: defineSlot({ id: 'H03', context: 'homepage-hero', label: 'Homepage hero poster', asset: assets.homepageFilmStill, approved: true }),
  homePersonalTraining: defineSlot({ id: 'H05', context: 'personal-training', label: 'Personal Training', asset: assets.dumbbells }),
  homeReformerPilates: defineSlot({ id: 'H06', context: 'reformer-pilates', label: 'Reformer Pilates', asset: assets.reformerStudio }),
  homeAquatics: defineSlot({ id: 'H07', context: 'aquatics', label: 'Aquatics', asset: assets.aquaticsDetail }),
  homeMx4: defineSlot({ id: 'H08', context: 'mx4', label: 'MX4', asset: assets.mx4Group }),
  homeKidsFamily: defineSlot({ id: 'H09', context: 'kids-family', label: 'Kids & Family' }),
  homeRecovery: defineSlot({ id: 'H10', context: 'recovery', label: 'Recovery' }),
  homeCardio: defineSlot({ id: 'H11', context: 'cardio', label: 'Cardio' }),
  homeMindBody: defineSlot({ id: 'H12', context: 'mind-body', label: 'Mind-Body' }),
  homeWeightTraining: defineSlot({ id: 'H13', context: 'weight-training', label: 'Weight Training' }),
  homeAllPrograms: defineSlot({ id: 'H14', context: 'programs-overview', label: 'All Programs' }),
  membershipHero: defineSlot({ id: 'membership-hero', context: 'membership', label: 'Membership' }),

  programsOverviewHero: defineSlot({ id: 'P00', context: 'programs-overview', label: 'Programs overview', asset: assets.adultTrainingFloor }),
  programsReformerPilates: defineSlot({ id: 'P01', context: 'reformer-pilates', label: 'Reformer Pilates', asset: assets.reformerStudio }),
  programsMx4: defineSlot({ id: 'P02', context: 'mx4', label: 'MX4', asset: assets.mx4Training }),
  programsPersonalTraining: defineSlot({ id: 'P03', context: 'personal-training', label: 'Personal Training', asset: assets.dumbbells }),
  programsSwimming: defineSlot({ id: 'P04', context: 'swimming', label: 'Swimming Lessons', asset: assets.aquaticsDetail }),
  programsKidsFamily: defineSlot({ id: 'P05', context: 'kids-family', label: 'Kids & Family' }),
  programsMassage: defineSlot({ id: 'P06', context: 'massage', label: 'Massage Therapy' }),
  programsCardio: defineSlot({ id: 'P07', context: 'cardio', label: 'Cardio' }),
  programsMindBody: defineSlot({ id: 'P08', context: 'mind-body', label: 'Mind & Body' }),

  aboutHero: defineSlot({ id: 'C01', context: 'about', label: 'About Delta Valley', asset: assets.adultTrainingFloor }),
  cardioHero: defineSlot({ id: 'C02', context: 'cardio', label: 'Cardio floor', asset: assets.cardioFloor }),
  cardioGroupClass: defineSlot({ id: 'C03', context: 'group-fitness', label: 'Group fitness class', asset: assets.groupBarbellClass }),
  groupFitnessHero: defineSlot({ id: 'C04', context: 'group-fitness', label: 'Group Fitness', asset: assets.groupBarbellClass }),
  kidsFamilyHero: defineSlot({ id: 'C05', context: 'kids-family', label: 'Kids & Family' }),
  partnershipsHero: defineSlot({ id: 'C06', context: 'partnerships', label: 'Community Partnerships' }),
  rentalsHero: defineSlot({ id: 'C07', context: 'rentals', label: 'Facility Rentals' }),

  amenitiesBasketball: defineSlot({ id: 'A01', context: 'basketball', label: 'Basketball Court' }),
  amenitiesAquatics: defineSlot({ id: 'A02', context: 'aquatics', label: 'Aquatics', asset: assets.aquaticsDetail }),
  amenitiesFacility: defineSlot({ id: 'A03', context: 'facility', label: 'Spacious Facility', asset: assets.cardioFloor }),
  amenitiesSauna: defineSlot({ id: 'A04', context: 'sauna', label: 'Sauna & Steam Rooms' }),
  amenitiesLockerRooms: defineSlot({ id: 'A05', context: 'locker-rooms', label: 'Private Locker Rooms' }),
  amenitiesRockwall: defineSlot({ id: 'A06', context: 'rockwall', label: 'Rockwall' }),
  amenitiesPickleball: defineSlot({ id: 'A07', context: 'pickleball', label: 'Pickleball' }),
  // Amenities journey (owner direction, Sept 30).
  amenitiesHero: defineSlot({ id: 'amenities-hero', context: 'amenities', label: 'Delta Valley Health Club' }),
  amenitiesStrength: defineSlot({ id: 'A09', context: 'strength', label: 'Strength floor' }),
  amenitiesCardio: defineSlot({ id: 'A10', context: 'cardio-floor', label: 'Cardio floor' }),
  legacyBlogUnverified: defineSlot({ id: 'B10', context: 'legacy-editorial', label: 'Archived article' }),
});

export const mediaDataProps = (media) => ({
  'data-media-slot': media.id,
  'data-media-status': media.status,
});
